/**
 * Approve / Reject for one improvement proposal: the final decision (`ready_for_human`) or the early design-review
 * gate (`specced` with an open design review). Reject asks for a rationale in a dialog.
 * Template example: NextAdmin demo `/ui-elements/dialogs` (dialog with a form) and `tables/basic-tables` row actions.
 */
import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, XCircle } from 'lucide-react';
import {
  decideImprovementProposalDesignMutation,
  decideImprovementProposalMutation,
  getImprovementProposalQueryKey,
  listImprovementProposalsQueryKey,
} from '../../generated/api/@tanstack/react-query.gen.js';
import { zImprovementProposalDecisionBody } from '../../generated/api/zod.gen.js';
import { Form, FormActions, TextAreaFormField, useZodForm } from '../../components/form/index.js';
import { Button, Modal, useToast } from '../../components/ui/index.js';
import { Can } from '../../components/auth/index.js';

interface ProposalDecisionActionsProps {
  proposalId: number;
  /** `final` decides a `ready_for_human` proposal; `design` decides the early design-review gate. */
  gate: 'final' | 'design';
  onDecided?: () => void;
}

const rejectSchema = zImprovementProposalDecisionBody.required({ rationale: true }).pick({ rationale: true });

const LABELS = {
  final: { approve: 'Approve', reject: 'Reject', rejectTitle: 'Reject proposal', done: 'Proposal' },
  design: { approve: 'Approve design', reject: 'Reject design', rejectTitle: 'Reject design', done: 'Design' },
} as const;

const REJECT_HINT = {
  final: 'Explain why this proposal is being rejected. It is used as a training signal for future proposals.',
  design: 'Explain why this design is rejected. The proposal will not proceed to implementation.',
} as const;

export function ProposalDecisionActions({ proposalId, gate, onDecided }: ProposalDecisionActionsProps) {
  const queryClient = useQueryClient();
  const { notify } = useToast();
  const [rejecting, setRejecting] = useState(false);
  const decideFinal = useMutation(decideImprovementProposalMutation());
  const decideDesign = useMutation(decideImprovementProposalDesignMutation());
  const form = useZodForm(rejectSchema, { defaultValues: { rationale: '' } });
  const labels = LABELS[gate];
  const isPending = decideFinal.isPending || decideDesign.isPending;

  const decide = async (decision: 'approve' | 'reject', rationale?: string) => {
    const request = { path: { id: proposalId }, body: { decision, rationale } };
    await (gate === 'design' ? decideDesign.mutateAsync(request) : decideFinal.mutateAsync(request));
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: listImprovementProposalsQueryKey() }),
      queryClient.invalidateQueries({ queryKey: getImprovementProposalQueryKey({ path: { id: proposalId } }) }),
    ]);
    notify(`${labels.done} ${decision === 'approve' ? 'approved' : 'rejected'}`);
    onDecided?.();
  };

  const approve = (event: React.MouseEvent) => {
    event.stopPropagation();
    decide('approve').catch(() => notify('Could not save the decision', 'danger'));
  };

  const closeReject = () => {
    setRejecting(false);
    form.reset();
  };

  return (
    // The row this sits in is clickable; presses on these controls (and on the dialog, which React bubbles through its
    // portal) must not open the row.
    <div className="flex justify-end gap-1" role="presentation" onClick={(event) => event.stopPropagation()}>
      <Can policy="CanDecideImprovements">
        <Button variant="success" size="sm" loading={isPending} onClick={approve}>
          <CheckCircle2 size={12} /> {labels.approve}
        </Button>
        <Button variant="danger" size="sm" disabled={isPending} onClick={() => setRejecting(true)}>
          <XCircle size={12} /> {labels.reject}
        </Button>
        <Modal open={rejecting} onClose={closeReject} title={labels.rejectTitle} description={REJECT_HINT[gate]}>
          <Form
            form={form}
            onSubmit={async ({ rationale }) => {
              await decide('reject', rationale.trim());
              closeReject();
            }}
            className="space-y-4"
          >
            <TextAreaFormField name="rationale" label="Rationale" rows={3} required />
            <FormActions submitLabel={labels.reject} onCancel={closeReject} />
          </Form>
        </Modal>
      </Can>
    </div>
  );
}
